import { useEffect, useId, useState } from 'react';
import type { ApiClient } from '../../../api/client';
import { text } from '../../../app/i18n';
import { Icon } from '../../../design-system/icons';
import type { Template } from '../layout-model';
import { Choice, Line, Sub } from './controls';
import type { Entry } from './widgets';

/**
 * Réglages « Personnes » d'un champ « Personne » (ADR-152 §6 ; planche « Panneaux-Personne ») : le rôle, choisi
 * par son libellé parmi ceux des couches actives (`ListPersonRoles`), groupés « Tiers » et « Employés » ; la valeur
 * au départ (« Du site », « Dernière », « Aucune ») ; la présentation (« Puce », « Champ ») ; les éléments qui
 * reprennent le nom choisi. Aucun code de rôle, aucun identifiant à l'écran : un rôle que les couches ne déclarent
 * plus se lit « Rôle inconnu » (le gate de publication le refuse).
 */

type Family = 'party_role' | 'employee_role';

export interface PersonRole {
  family: Family;
  role: string;
  label: Readonly<Record<string, string>>;
  origin: 'core' | 'pack' | 'customer' | 'solution';
  people?: number;
}

const FAMILIES: ReadonlyArray<{ family: Family; label: string; reference: string }> = [
  { family: 'party_role', label: 'Tiers', reference: 'Party' },
  { family: 'employee_role', label: 'Employés', reference: 'Identity' },
];

let rolesCache: { client: ApiClient; loading: Promise<readonly PersonRole[]> } | null = null;

/** Vide le cache des rôles (tests). */
export function clearPersonRoles(): void {
  rolesCache = null;
}

function loadRoles(client: ApiClient): Promise<readonly PersonRole[]> {
  if (rolesCache?.client === client) return rolesCache.loading;
  const loading = client.query<{ roles: PersonRole[] }>('ListPersonRoles').then((response) => response.data.roles ?? []);
  rolesCache = { client, loading };
  loading.catch(() => {
    if (rolesCache?.loading === loading) rolesCache = null;
  });
  return loading;
}

type RolesState = { status: 'loading' } | { status: 'ready'; roles: readonly PersonRole[] } | { status: 'unavailable' };

export function usePersonRoles(client: ApiClient | undefined): RolesState {
  const [state, setState] = useState<RolesState>(client === undefined ? { status: 'unavailable' } : { status: 'loading' });
  useEffect(() => {
    if (client === undefined) {
      setState({ status: 'unavailable' });
      return;
    }
    let alive = true;
    loadRoles(client).then(
      (roles) => alive && setState({ status: 'ready', roles }),
      () => alive && setState({ status: 'unavailable' }),
    );
    return () => {
      alive = false;
    };
  }, [client]);
  return state;
}

interface PickOption {
  value: string;
  label: string;
  hint?: string;
}

/** Choix multiple compact : un bouton qui résume, une liste groupée à cocher qui s'ouvre dessous. */
function MultiPick({ label, summary, groups, selected, onToggle, disabled = false }: { label: string; summary: string; groups: ReadonlyArray<{ label: string; options: readonly PickOption[] }>; selected: ReadonlySet<string>; onToggle: (value: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="ay-v2-pick">
      <button type="button" className="ay-v2-pick__button" aria-label={label} aria-haspopup="listbox" aria-expanded={open} aria-controls={id} disabled={disabled} onClick={() => setOpen(!open)} onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}>
        <span className="ay-v2-pick__summary">{summary}</span>
        <Icon name="chevron-down" size={12} />
      </button>
      {open && (
        <span id={id} className="ay-v2-pick__list" role="listbox" aria-label={label} aria-multiselectable="true" onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}>
          {groups
            .filter((group) => group.options.length > 0)
            .map((group) => (
              <span key={group.label} role="group" aria-label={group.label} className="ay-v2-pick__group">
                <span className="ay-v2-pick__heading" aria-hidden="true">
                  {group.label}
                </span>
                {group.options.map((option) => (
                  <button key={option.value} type="button" role="option" aria-selected={selected.has(option.value)} title={option.hint} onClick={() => onToggle(option.value)}>
                    <span className="ay-v2-pick__check" aria-hidden="true">
                      {selected.has(option.value) && <Icon name="check" size={12} />}
                    </span>
                    {option.label}
                  </button>
                ))}
              </span>
            ))}
        </span>
      )}
    </span>
  );
}

type Source = { family: Family; roles: string[] };
type Json = Record<string, unknown>;

const roleKey = (family: Family, role: string): string => `${family}:${role}`;

/** Change la source : un rôle d'une autre famille la remplace (et règle le type cité) ; sinon il s'ajoute ou se retire. */
export function toggleRole(entry: Entry, family: Family, role: string): Entry {
  const field = { ...(entry.field as unknown as Json) };
  const source = field['person_source'] as Source | undefined;
  let next: Source | undefined;
  if (source === undefined || source.family !== family) next = { family, roles: [role] };
  else {
    const roles = source.roles.includes(role) ? source.roles.filter((candidate) => candidate !== role) : [...source.roles, role];
    next = roles.length === 0 ? undefined : { family, roles };
  }
  if (next === undefined) delete field['person_source'];
  else field['person_source'] = next;
  field['reference_type'] = FAMILIES.find((candidate) => candidate.family === family)!.reference;
  return { ...entry, field } as unknown as Entry;
}

/** Éléments qui peuvent reprendre le nom : les autres champs, groupés par partie, et les parties. */
function echoGroups(template: Template, self: string): Array<{ label: string; options: PickOption[] }> {
  const groups = template.sections.map((section) => ({
    label: text(section.label, 'fr', 'Partie'),
    options: section.fields.filter((entry) => entry.field.field_id !== self).map((entry) => ({ value: entry.field.field_id, label: text(entry.field.label, 'fr', 'Champ') })),
  }));
  const parts = { label: 'Parties', options: template.sections.map((section) => ({ value: section.section_id, label: text(section.label, 'fr', 'Partie') })) };
  return [...groups, parts];
}

export function echoLabel(template: Template, ids: readonly string[]): string {
  if (ids.length === 0) return 'Nulle part';
  if (ids.length > 1) return `${ids.length} éléments`;
  const id = ids[0]!;
  for (const section of template.sections) {
    const entry = section.fields.find((candidate) => candidate.field.field_id === id);
    if (entry !== undefined) return `Sous-titre de « ${text(entry.field.label, 'fr', 'Champ')} »`;
  }
  const section = template.sections.find((candidate) => candidate.section_id === id);
  return section === undefined ? 'Élément retiré' : `Sous-titre de « ${text(section.label, 'fr', 'Partie')} »`;
}

const START_OPTIONS = [
  { value: 'site' as const, label: 'Du site', hint: 'La personne attachée au site pour ce rôle' },
  { value: 'last' as const, label: 'Dernière', hint: 'La dernière personne choisie sur cet appareil' },
  { value: 'none' as const, label: 'Aucune', hint: 'L’opérateur choisit à chaque fiche' },
];

export function PersonSettings({ template, entry, client, set, patch }: { template: Template; entry: Entry; client: ApiClient | undefined; set: (next: Entry) => void; patch: (pointer: string, value: unknown) => void }) {
  const roles = usePersonRoles(client);
  const field = entry.field as unknown as { person_source?: Source; person_default?: 'site' | 'last' | 'none' };
  const source = field.person_source;
  const presentation = (entry.presentation ?? {}) as Json;
  const known = roles.status === 'ready' ? roles.roles : [];
  const labelOf = (family: Family, role: string): string => {
    const found = known.find((candidate) => candidate.family === family && candidate.role === role);
    return found === undefined ? (roles.status === 'loading' ? '…' : 'Rôle inconnu') : text(found.label, 'fr', 'Rôle');
  };
  const roleSummary = source === undefined || source.roles.length === 0 ? 'Choisir…' : source.roles.map((role) => labelOf(source.family, role)).join(', ');
  const selected = new Set(source === undefined ? [] : source.roles.map((role) => roleKey(source.family, role)));
  const groups = FAMILIES.map(({ family, label }) => ({
    label,
    options: known
      .filter((candidate) => candidate.family === family)
      .map((candidate) => ({ value: roleKey(family, candidate.role), label: text(candidate.label, 'fr', 'Rôle'), ...(candidate.people === undefined ? {} : { hint: `${candidate.people} personne${candidate.people > 1 ? 's' : ''}` }) })),
  }));
  const into = ((presentation['echo'] as { into?: string[] } | undefined)?.into ?? []) as string[];
  const variant = presentation['variant'] === 'chip' ? 'chip' : 'field';
  return (
    <Sub>
      <Line label="Rôle" help="Les personnes proposées : les tiers ou les employés qui ont ce rôle.">
        <MultiPick
          label="Rôle"
          summary={roles.status === 'unavailable' && source === undefined ? 'Liste indisponible' : roleSummary}
          groups={groups}
          selected={selected}
          disabled={roles.status !== 'ready'}
          onToggle={(value) => {
            const [family, role] = value.split(':') as [Family, string];
            set(toggleRole(entry, family, role));
          }}
        />
      </Line>
      <Choice label="Au départ" value={field.person_default ?? 'none'} options={START_OPTIONS} onChange={(value) => patch('/field/person_default', value === 'none' ? undefined : value)} />
      <Choice
        label="Présentation"
        value={variant}
        options={[
          { value: 'chip', label: 'Puce', hint: 'Avatar et nom, dans un bandeau' },
          { value: 'field', label: 'Champ', hint: 'Un champ de sélection avec avatar' },
        ]}
        onChange={(value) => patch('/presentation/variant', value)}
      />
      <Line label="Repris dans" help="Le nom choisi s’affiche en sous-titre de ces éléments.">
        <MultiPick
          label="Repris dans"
          summary={echoLabel(template, into)}
          groups={echoGroups(template, entry.field.field_id)}
          selected={new Set(into)}
          onToggle={(value) => {
            const next = into.includes(value) ? into.filter((candidate) => candidate !== value) : [...into, value];
            patch('/presentation/echo', next.length === 0 ? undefined : { into: next });
          }}
        />
      </Line>
    </Sub>
  );
}
